/**
 * The note box's pure logic (repo convention: logic in .ts, components in
 * .tsx): newest-first ordering and the words for a refused save.
 */
import type { DoorNoteRead } from '../../services/qcpInvestigations';

/** The server's ceiling on a note (characters, after trimming;
 *  services/investigations/person_notes.py NOTE_MAX_CHARS). */
export const NOTE_MAX_CHARS = 4000;

/** Newest first; a same-instant pair keeps the order it came in. */
export function newestFirst(notes: ReadonlyArray<DoorNoteRead>): DoorNoteRead[] {
  return notes
    .map((note, i) => ({ note, i, t: new Date(note.created_at).getTime() }))
    .sort((a, b) => (Number.isNaN(b.t) ? 0 : b.t) - (Number.isNaN(a.t) ? 0 : a.t) || a.i - b.i)
    .map(({ note }) => note);
}

/**
 * Why a note did not save, in the reader's words. The server refuses a bad
 * note with 422 and a reason ("A note is capped at 4000 characters."),
 * either as a string or inside a validation list; anything else is a
 * failure to reach it. The typed text is always kept, so the line says so.
 */
export function noteSaveError(error: unknown): string {
  const response = (error as { response?: { status?: number; data?: { detail?: unknown } } })?.response;
  const detail = response?.data?.detail;
  let reason = '';
  if (typeof detail === 'string') reason = detail.trim();
  else if (Array.isArray(detail)) {
    const first = detail.find((d): d is { msg: string } => typeof (d as { msg?: unknown })?.msg === 'string');
    reason = (first?.msg ?? '').replace(/^Value error,\s*/i, '').trim();
  }
  if (reason) return `Not saved: ${reason} Your text is still here.`;
  return 'Not saved: the server could not be reached. Your text is still here; try Save again.';
}

/**
 * The newest note as one line for a list row or card: "Jess Finlaw: Called
 * Dave, no answer" (+ "· 2 more notes"), first line only, cut near `max`.
 * Null when there is no note.
 */
export function latestNoteLine(
  note: DoorNoteRead | null | undefined,
  count = 0,
  max = 120,
): string | null {
  if (!note) return null;
  const first = note.text.split('\n').map((l) => l.trim()).find(Boolean) ?? '';
  let text = first;
  if (text.length > max) {
    const cut = text.slice(0, max - 1);
    const space = cut.lastIndexOf(' ');
    text = `${(space > max / 2 ? cut.slice(0, space) : cut).trimEnd()}\u2026`;
  }
  const more = count > 1 ? ` · ${count - 1} more note${count - 1 === 1 ? '' : 's'}` : '';
  return `${note.author_label}: ${text}${more}`;
}
